import type { TaxonomyOrgRow } from '@risksmart-app/trpc/src/types';
import type { GetTaxonomyByLocaleAndOrgQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetTaxonomyByLocaleAndOrgDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

type UseGetTaxonomyByLocaleArgs = {
  locale: string;
  orgKey: string;
};

/**
 * Org i18n taxonomy for a locale (RSP-6214). The tRPC procedure scopes by the
 * caller's org server-side, so `orgKey` is only used to feed the legacy
 * GraphQL `where` variables on the (flag-off) Apollo branch — it is never sent
 * to tRPC.
 */
export const useGetTaxonomyByLocale = createQueryHook<
  UseGetTaxonomyByLocaleArgs,
  TaxonomyOrgRow | null,
  GetTaxonomyByLocaleAndOrgQuery
>({
  trpcQueryOptions: (trpc, { locale }) =>
    trpc.frontend.taxonomy.taxonomyByLocale.queryOptions({ locale }),
  // Legacy query root is `taxonomy_org` (0..1; the provider reads
  // `[0]?.taxonomy`). The provider never reads `organisations_aggregate`, so
  // the count is stamped 0 purely to satisfy the generated shape rather than
  // paying for an org-count sub-query on this i18n hot path.
  mapTrpcDataToGraphQL: (data) => ({
    taxonomy_org:
      data && data.taxonomy
        ? [
            {
              Id: data.Id,
              Locale: data.Locale,
              OrgName: data.OrgName,
              TaxonomyId: data.TaxonomyId,
              taxonomy: {
                Common: data.taxonomy.Common,
                Description: data.taxonomy.Description,
                ModifiedAtTimestamp: data.taxonomy.ModifiedAtTimestamp,
                Id: data.taxonomy.Id,
                Library: data.taxonomy.Library,
                Rating: data.taxonomy.Rating,
                Taxonomy: data.taxonomy.Taxonomy,
                InternalAuditRating: data.taxonomy.InternalAuditRating,
                OversightRating: data.taxonomy.OversightRating,
                organisations_aggregate: { aggregate: { count: 0 } },
              },
            },
          ]
        : [],
  }),
  graphqlDocument: GetTaxonomyByLocaleAndOrgDocument,
  graphqlVariables: ({ locale, orgKey }) => ({
    Locale: locale,
    OrgKey: orgKey,
  }),
});
